import PropTypes from 'prop-types';

const WIDTH_CLASSES = {
	default: 'max-w-7xl',
	full: 'max-w-[1920px]',
};

/**
 * Scaffold for every routed page inside AppShell. Owns the page-level concerns the
 * shell deliberately doesn't: padding, content max-width and scroll behaviour.
 *
 * @param {Object} props
 * @param {import('react').ReactNode} [props.header] - Usually a PageHeader; in 'content' mode it stays fixed while the content scrolls.
 * @param {'page'|'content'|'bare'} [props.scrollMode] - 'page' (default): the whole page scrolls, header included.
 *   'content': the page fills the viewport exactly; children sit in a flex-1 min-h-0 column and manage their own
 *   scrolling (growing elements use flex-1 min-h-0). 'bare': full-bleed fill without padding or max-width; header is not rendered.
 * @param {'default'|'full'} [props.width] - 'default' (1280px) suits forms, settings and detail views; 'full' (1920px)
 *   is for pages whose main content is a table, board, calendar or map. Ignored in 'bare' mode.
 * @param {string} [props.className] - Extra classes for the root element (e.g. a page background).
 */
const Page = ({ header, scrollMode = 'page', width = 'default', className = '', children }) => {
	const widthClass = `${WIDTH_CLASSES[width]} mx-auto w-full`;

	if (scrollMode === 'bare') {
		return <div className={`h-full ${className}`}>{children}</div>;
	}

	if (scrollMode === 'content') {
		return (
			<div className={`h-full p-4 ${className}`}>
				<div className={`h-full ${widthClass} flex flex-col`}>
					{header}
					<div className="flex-1 min-h-0 flex flex-col">{children}</div>
				</div>
			</div>
		);
	}

	return (
		<div className={`h-full overflow-y-auto p-4 ${className}`}>
			<div className={widthClass}>
				{header}
				{children}
			</div>
		</div>
	);
};

Page.propTypes = {
	header: PropTypes.node,
	scrollMode: PropTypes.oneOf(['page', 'content', 'bare']),
	width: PropTypes.oneOf(['default', 'full']),
	className: PropTypes.string,
	children: PropTypes.node,
};

export default Page;
